import React, { useState } from 'react';
import { Checkbox, Col, Form, Input, Row } from 'antd';
import CategoryTitle from './CategoryTitle';
import styled from 'styled-components';
import Section from 'components/Section';
import CheckboxItem from './CheckboxItem';
import ImageItem from './ImageItem';

export const Text = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    font-weight: 300;
    word-wrap: break-word;
    -webkit-line-clamp: 4; /* number of lines to show */
    -webkit-box-orient: vertical;
`;

const TextArea = styled(Input.TextArea)`
    max-width: 100%;
    transition:
        all 0.3s,
        height 0s !important;
    border: 1px solid ${({ theme }) => theme.colors.brownishGrey} !important;
    border-radius: 2px;
    box-sizing: border-box;
    width: 100%;
`;

const StyledRow = styled(Row)`
    background: ${(props) => props.color || props.theme.colors.grey};
    width: 100%;
`;

type Props = {
    category: QuizCategory;
    showHints: boolean;
};

const QuizCategory = ({ category, showHints }: Props): JSX.Element => {
    const [votes, setVotes] = useState<number>(0);
    const [isTouched, setIsTouched] = useState<boolean>(false);

    const showHint = showHints || (isTouched && category.showHintOnAnswer);

    if (category.type === 'textarea') {
        const handleTouch = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            autoResize(event.target as HTMLTextAreaElement);
            if (isTouched) return;
            setIsTouched(true);
        };

        const autoResize = (oField: HTMLTextAreaElement) => {
            if (oField.scrollHeight > oField.clientHeight) {
                oField.style.height = `${oField.scrollHeight}px`;
            }
        };

        return (
            <Col span={24} id={`category__${category.id}`}>
                <CategoryTitle category={category} />
                <Text dangerouslySetInnerHTML={{ __html: category.description }} />{' '}
                <Form.Item
                    name={['votes', `${category.id}`]}
                    rules={[{ required: category.isRequired }]}
                >
                    <TextArea rows={5} showCount onKeyUp={handleTouch} />
                </Form.Item>
                {showHint && (
                    <Section>
                        <Col>
                            <div dangerouslySetInnerHTML={{ __html: category.hint }} />
                        </Col>
                    </Section>
                )}
            </Col>
        );
    }

    if ((category.questions || []).length === 0) {
        return null;
    }

    const handleVote = () => {
        if (category.maxVotes > 0 && category.maxVotes === votes) {
            return;
        }
        setIsTouched(true);
        setVotes((prev) => prev + 1);
    };

    const handleUnVote = () => {
        setVotes((prev) => prev - 1);
    };

    const isLocked = category.maxVotes > 0 && category.maxVotes === votes;

    const highestRate = Math.max(...category.questions.map((question) => question.rating));

    if (category.type === 'checkbox') {
        return (
            <Col span={24} id={`category__${category.id}`}>
                <CategoryTitle category={category} categoryVotes={votes} />
                <Text dangerouslySetInnerHTML={{ __html: category.description }} />
                <Form.Item
                    name={['votes', `${category.id}`]}
                    rules={[{ required: category.isRequired }]}
                >
                    <Checkbox.Group style={{ width: '100%' }}>
                        <StyledRow>
                            {category.questions.map((item: ContestQuestion) => (
                                <Col
                                    span={24}
                                    id={`category__${category.id}__${item.slug}`}
                                    key={`category__${category.id}__${item.slug}`}
                                >
                                    <CheckboxItem
                                        $item={item}
                                        onVote={handleVote}
                                        onUnVote={handleUnVote}
                                        $isLocked={isLocked}
                                        $canUnVote={!category.showCorrectAnswer}
                                        $showCorrectAnswer={isLocked && category.showCorrectAnswer}
                                        $showResult={isLocked && category.showResult}
                                        $isBestAnswer={highestRate === item.rating}
                                    />
                                </Col>
                            ))}
                        </StyledRow>
                    </Checkbox.Group>
                </Form.Item>
                {category.showHintOnAnswer && isTouched && (
                    <Section>
                        <Col>
                            <div dangerouslySetInnerHTML={{ __html: category.hint }} />
                        </Col>
                    </Section>
                )}
            </Col>
        );
    }
    return (
        <Col span={24} id={`category__${category.id}`}>
            <CategoryTitle category={category} categoryVotes={votes} />
            <Text dangerouslySetInnerHTML={{ __html: category.description }} />
            <Form.Item
                name={['votes', `${category.id}`]}
                rules={[{ required: category.isRequired }]}
            >
                <Checkbox.Group style={{ width: '100%' }}>
                    <StyledRow gutter={[32, 48]}>
                        {category.questions.map((item: ContestQuestion) => (
                            <Col
                                id={`category__${category.id}__${item.slug}`}
                                key={`category__${category.id}__${item.slug}`}
                                style={{ margin: '0 auto' }}
                            >
                                <ImageItem
                                    $item={item}
                                    onVote={handleVote}
                                    onUnVote={handleUnVote}
                                    $isLocked={isLocked}
                                    $canUnVote={!category.showCorrectAnswer}
                                    $showCorrectAnswer={isLocked && category.showCorrectAnswer}
                                    $showResult={isLocked && category.showResult}
                                    $isBestAnswer={highestRate === item.rating}
                                />
                            </Col>
                        ))}
                    </StyledRow>
                </Checkbox.Group>
            </Form.Item>
            {category.showHintOnAnswer && isTouched && (
                <Section>
                    <Col>
                        <div dangerouslySetInnerHTML={{ __html: category.hint }} />
                    </Col>
                </Section>
            )}
        </Col>
    );
};

export default QuizCategory;
